import { DEFAULT_POINT_STATUSES } from 'application/pages/PointsPage/renderer/PointsToolbar/consts';
import { useAccountCompanyIds } from 'application/hooks';
import useAllPointList from 'domain/point/useAllPointList';
import { useCallback, useEffect, useState } from 'react';
import { usePointsGroupQuery, usePointsGroupUpdateMutation } from 'domain/api/graphql/generated';
import { NOTIFICATION_DEFAULT_ERROR_MESSAGE, useNotificationContext } from 'application/providers/NotificationProvider';
import { FetchPolicyKeys } from 'domain/api/graphql/types';
import { useIsPointsListChanged } from '../hooks/useIsPointsListChanged';
import { PointsListDataItem } from '../PointsGroupsPointsList/types';
import { TEXTS } from '../../texts';

interface IUseSelectPointsGroupPointsViewModel {
  devicesGroupId: string;
}

const BINDED_TO_OTHER_GROUPS = /binded to other groups/;
const PLAY_DURATION_ALIGNMENT = /play_duration_alignment/;

export const useSelectPointsGroupPointsViewModel = ({ devicesGroupId }: IUseSelectPointsGroupPointsViewModel) => {
  const companyIds = useAccountCompanyIds();
  const allPointList = useAllPointList({
    companyIds,
    playingStatuses: DEFAULT_POINT_STATUSES,
    hasVideoSupport: true,
    withDevices: true,
  });

  const notification = useNotificationContext();

  const { data, loading } = usePointsGroupQuery({
    variables: { id: devicesGroupId! },
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
    skip: !devicesGroupId,
  });

  const [selectedPoints, setSelectedPoints] = useState<PointsListDataItem[]>([]);

  const [mutate, mutationResult] = usePointsGroupUpdateMutation();
  const [initialSelectedPoints, setInitialSelectedPoints] = useState<PointsListDataItem[]>([]);

  const updatePointsGroup = async () => {
    const devicesMap = allPointList.points?.reduce(
      (acc, it) => {
        if (it?.device?.id) {
          acc[it.id] = it.device?.id;
        }

        return acc;
      },
      {} as { [key: string]: string }
    );
    const deviceIds = selectedPoints?.map((it) => devicesMap?.[it.id]).filter(Boolean) as string[];

    if (devicesGroupId && deviceIds) {
      const result = await mutate({
        variables: {
          input: {
            devicesGroupId,
            deviceIds,
            masterVideoStreamId: data?.smartlkDevicesGroupQuery.masterVideoStream?.id,
          },
        },
      });

      if (result.data?.smartlkDevicesGroupUpdate?.ok) {
        notification.showInfoNotification({
          children: TEXTS.SELECT_POINTS_SUCCESS,
        });
      } else {
        const message = result?.data?.smartlkDevicesGroupUpdate?.errors?.[0]?.messages?.[0];
        if (message && (BINDED_TO_OTHER_GROUPS.test(message) || PLAY_DURATION_ALIGNMENT.test(message))) {
          notification.showErrorNotification({
            children: TEXTS.BINDED_TO_OTHER_GROUPS_ERROR,
          });

          return;
        }

        notification.showErrorNotification({
          children:
            result?.data?.smartlkDevicesGroupUpdate?.errors?.[0]?.messages?.[0] || NOTIFICATION_DEFAULT_ERROR_MESSAGE,
        });
      }
    }
  };

  const onPointsChange = (pointsIds: PointsListDataItem[]) => {
    if (!allPointList.points) return;

    const pointsSet = new Set(pointsIds.map(({ id }) => String(id)));
    setSelectedPoints(
      allPointList.points
        ?.filter((point) => pointsSet.has(String(point.id)))
        .map((it) => ({
          ...it,
          disabled: Boolean(it.device?.group?.id && it.device?.group?.id !== devicesGroupId),
        }))
    );
  };

  useEffect(() => {
    const initialPoints = data?.smartlkDevicesGroupQuery.devices?.reduce((acc, it) => {
      if (it.point?.id) {
        acc.push({ id: it.point.id });
      }

      return acc;
    }, [] as PointsListDataItem[]);

    if (initialPoints && initialPoints.length) {
      onPointsChange(initialPoints);
      setInitialSelectedPoints(initialPoints);
    }
  }, [data?.smartlkDevicesGroupQuery.devices, allPointList.points]);

  const isButtonDisabled = useIsPointsListChanged(selectedPoints, initialSelectedPoints);

  useEffect(() => {
    if (companyIds.length > 0) {
      allPointList.request();
    }
  }, [companyIds]);

  const loadMore = useCallback(async () => {
    if (allPointList.loading || !allPointList.paginationInfo?.hasNextPage) {
      return;
    }

    allPointList.fetchMore(allPointList.paginationInfo?.page);
  }, [allPointList]);

  return {
    allPointList,
    loadMore,
    isButtonDisabled,
    onPointsChange,
    updatePointsGroup,
    selectedPoints,
    isButtonLoading: mutationResult.loading || loading,
  };
};
